/**
 * Keeps a curated deck from running out.
 *
 * The failure this exists to remove: MAHI's search handed the stack whatever
 * one tool call returned, the shopper swiped through it in under a minute, and
 * the screen said "You're all caught up" — on a catalogue of 1,500. Read as
 * *this app has nothing*, which is the most expensive thing this screen can
 * say, and it was said about a filter rather than about the shop.
 *
 * So a curated deck is now a moving window. This hook watches how many cards
 * are left ahead of the shopper and tops the deck up before they can reach the
 * end, by asking `/inventory/more` for the next rung of the backend's widening
 * ladder (see services/mahi/widen.py): the same search paged deeper for as long
 * as it has anything, then progressively broader searches that each keep a real
 * anchor from what was asked for.
 *
 * **The transition is the point, not the top-up.** When a batch comes back
 * having crossed into broader territory, the server sends MAHI's line about
 * having done so, and this hook reports it as a `transition` rather than
 * appending silently. DiscoveryFeed shows it as a card the shopper agrees to.
 * Quietly substituting near-misses for what someone asked for is exactly the
 * behaviour that erodes trust in a search — the same reason the feed announces
 * its own widening (feed.py) instead of just serving lehengas to a saree
 * shopper.
 *
 * The default (non-curated) feed does not come through here: it is not scoped
 * to a request that can be widened *away from*, so there is no "what they
 * asked for" to stay anchored to and no transition to agree to. It gets the
 * same moving window without the ladder — see useFeedRefill.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMoreDeck } from '../../api';
import { shouldShowNotice, markNoticeShown } from './discoverySession';

// Fetch once the shopper is this close to the end. Comfortably more than the
// three cards the stack renders, so the next batch has landed long before it
// could be noticed as a gap — and small enough that a deck nobody swipes to the
// end of never costs a request.
export const REFILL_AT = 6;

// A batch smaller than this is treated as the ladder's last word even without
// the server saying `exhausted` — it means every remaining rung together could
// not fill one page, so there is nothing behind the next fetch either.
const LAST_BATCH = 3;

// How much swipe history one request carries. Under the route's own cap, so a
// long session degrades by forgetting its oldest cards rather than by having
// the request rejected and the deck dead-ending — which is the exact failure
// this hook exists to prevent, arriving at card 600 instead of card 8. The
// dropped skus are from rungs that are long exhausted anyway.
const MAX_EXCLUDE = 500;

// Consecutive failed top-ups before this gives up on the deck. One retry
// covers a dropped request; retrying forever would mean a request per swipe
// at exactly the moment the network is already unhappy.
const MAX_FAILURES = 2;

export default function useDeckContinuation({ source, deckKey, deck, index, onAppend }) {
  // Which rung the deck is currently on, and everything it has already shown.
  // Both live in refs: they change on every append and nothing renders from
  // them, so making them state would re-render the whole stack per fetch.
  const tierRef = useRef(0);
  const shownRef = useRef([]);
  const inFlightRef = useRef(false);
  const doneRef = useRef(false);
  const failuresRef = useRef(0);
  const sourceKeyRef = useRef(null);

  // MAHI's line about having widened, held until the shopper reaches the card
  // it belongs in front of. `{ message, atIndex }` — atIndex is where the
  // widened products start, so the card appears exactly at the seam rather
  // than the moment the fetch happens to resolve.
  const [transition, setTransition] = useState(null);

  // Identity of the *deck*, not of the search behind it. A refine keeps the
  // same conversation id while replacing every product in the stack, so keying
  // this on the source alone would carry the old deck's rung and exclusions
  // into a hunt that has just been narrowed — the new search would start
  // already widened. DiscoveryFeed hands over a fresh `deckKey` per handoff.
  const sourceKey = source ? `${deckKey}:${JSON.stringify(source)}` : '';

  // A new deck is a new ladder: everything the previous one had shown, how far
  // up it climbed, and any transition it was still holding all belong to a
  // question the shopper has stopped asking.
  useEffect(() => {
    if (sourceKeyRef.current === sourceKey) return;
    sourceKeyRef.current = sourceKey;
    tierRef.current = 0;
    shownRef.current = [];
    doneRef.current = false;
    failuresRef.current = 0;
    setTransition(null);
  }, [sourceKey]);

  const dismissTransition = useCallback(() => setTransition(null), []);

  useEffect(() => {
    if (!source) return;
    if (doneRef.current || inFlightRef.current) return;
    if (deck.length - index > REFILL_AT) return;

    // Deliberately not cancelled on cleanup. Every swipe changes `index` and so
    // re-runs this effect, and a fetch abandoned on cleanup would be re-issued
    // on the very next swipe — one request per card until the batch landed. The
    // in-flight guard is what makes it exactly one request per top-up; the
    // key check at the bottom is what keeps a late reply from a search the
    // shopper has moved on from out of the current deck.
    const requestedFor = sourceKey;
    inFlightRef.current = true;
    // Everything on screen so far, not just this batch — the server needs the
    // full exclusion set to know what "deeper" means.
    const exclude = Array.from(new Set([
      ...shownRef.current,
      ...deck.map((p) => p.mira_sku),
    ])).filter(Boolean).slice(-MAX_EXCLUDE);
    const seam = deck.length;

    (async () => {
      const result = await fetchMoreDeck({ ...source, exclude, tier: tierRef.current });
      inFlightRef.current = false;
      if (sourceKeyRef.current !== requestedFor) return;

      if (!result) {
        // The request itself failed. Distinct from the server saying there is
        // nothing left: one dropped call should cost a retry, not the rest of
        // the deck. The next swipe re-runs this effect and tries again.
        failuresRef.current += 1;
        if (failuresRef.current >= MAX_FAILURES) doneRef.current = true;
        return;
      }
      failuresRef.current = 0;

      const items = result.items || [];
      if (!items.length) {
        // Genuinely nothing left anywhere on the ladder. "You're all caught up"
        // is still reachable — it just has to be true now.
        doneRef.current = true;
        return;
      }
      tierRef.current = result.tier ?? tierRef.current;
      shownRef.current = [...exclude, ...items.map((p) => p.mira_sku)];
      if (result.exhausted || items.length <= LAST_BATCH) doneRef.current = true;
      // Pinned to where the widened products actually start, so the card
      // appears at the seam rather than wherever the shopper happened to be
      // when the fetch resolved. Gated on shouldShowNotice for the same
      // reason DiscoveryFeed's own "running low" toast is: a remount (the
      // shopper detoured through the wardrobe or profile and came back)
      // used to be able to re-fetch the exact same widened rung and hand
      // back a transition card MAHI had already said once this session.
      if (result.message && shouldShowNotice(result.message)) {
        setTransition({ message: result.message, atIndex: seam });
        markNoticeShown(result.message);
      }
      onAppend(items);
    })();
    // `deck.length` rather than `deck`: a new array identity with the same
    // length is the same deck as far as running low is concerned.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, deck.length, index, source]);

  return { transition, dismissTransition };
}
